import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { MedaImage } from '../types/image';
import { imageStore, generateThumbnail, getImageDimensions } from '../services/imageStore';

interface LibraryContextValue {
  images: MedaImage[];
  loading: boolean;
  addImage(blob: Blob, name: string, source?: MedaImage['source'], parentId?: string, operation?: string): Promise<MedaImage>;
  removeImage(id: string): Promise<void>;
  toggleFavorite(id: string): Promise<void>;
  updateImage(id: string, patch: Partial<Pick<MedaImage, 'name' | 'tags' | 'favorite'>>): Promise<void>;
  refresh(): Promise<void>;
  getImage(id: string): Promise<MedaImage | undefined>;
}

const LibraryContext = createContext<LibraryContextValue | null>(null);

export function useLibrary(): LibraryContextValue {
  const ctx = useContext(LibraryContext);
  if (!ctx) throw new Error('useLibrary must be used within LibraryProvider');
  return ctx;
}

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

export function LibraryProvider({ children }: { children: ReactNode }) {
  const [images, setImages] = useState<MedaImage[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const all = await imageStore.getAll();
    setImages(all);
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const addImage = useCallback(async (
    blob: Blob,
    name: string,
    source: MedaImage['source'] = 'upload',
    parentId?: string,
    operation?: string,
  ): Promise<MedaImage> => {
    const [thumbnail, dims] = await Promise.all([
      generateThumbnail(blob),
      getImageDimensions(blob),
    ]);
    const image: MedaImage = {
      id: generateId(),
      name,
      blob,
      thumbnail,
      width: dims.width,
      height: dims.height,
      source,
      parentId,
      operation,
      tags: [],
      favorite: false,
      createdAt: Date.now(),
    };
    await imageStore.save(image);
    setImages(prev => [image, ...prev]);
    return image;
  }, []);

  const removeImage = useCallback(async (id: string) => {
    await imageStore.delete(id);
    setImages(prev => prev.filter(img => img.id !== id));
  }, []);

  const toggleFavorite = useCallback(async (id: string) => {
    const newVal = await imageStore.toggleFavorite(id);
    setImages(prev => prev.map(img =>
      img.id === id ? { ...img, favorite: newVal } : img
    ));
  }, []);

  const getImage = useCallback(async (id: string) => {
    return imageStore.get(id);
  }, []);

  const updateImage = useCallback(async (
    id: string,
    patch: Partial<Pick<MedaImage, 'name' | 'tags' | 'favorite'>>,
  ) => {
    const existing = await imageStore.get(id);
    if (!existing) return;
    const next: MedaImage = { ...existing, ...patch };
    await imageStore.save(next);
    setImages(prev => prev.map(img => img.id === id ? { ...img, ...patch } : img));
  }, []);

  return (
    <LibraryContext.Provider value={{ images, loading, addImage, removeImage, toggleFavorite, updateImage, refresh, getImage }}>
      {children}
    </LibraryContext.Provider>
  );
}
